import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
  getBezierPath,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { apiCall, apiCallJson } from './Api';
import { getCurrentToken } from './SessionManager';
import {
  Save, Play, Plus, Trash2, Upload, Download, Workflow, Zap, X,
  List, ArrowLeft, Search, Calendar, BarChart3, Edit3, Copy,
  RefreshCw, Filter, Code, Database, FileText, MessageSquare,
  MousePointer, Image, Video, BoomBox, MoreHorizontal, Layers,
  Send, RotateCcw, CheckCircle, Key, Eye, EyeOff, ExternalLink,
  AlertCircle, CheckCircle2, Undo2, Redo2, ClipboardList, Bot,
  UserCheck, Paperclip, ShieldCheck, Users, Radio, Globe,
  ChevronRight, ChevronDown, FolderOpen, FolderClosed, Maximize2,
  Minimize2, Map, Navigation, Target, ZapOff, LayoutGrid, GitBranch, ListOrdered,
  Clock, History, Crosshair, ChevronsUp, ChevronsDown, Palette,
  SlidersHorizontal, PanelLeftClose, PanelLeftOpen, ArrowRight,
  Hash, Tag, Route, Waypoints, Sparkles, Compass, Move,
  PhoneCall, Bell, HardDrive,
} from 'lucide-react';
import WorkflowChatPreview from './WorkflowChatPreview';
import AgentHandoffDashboard from './AgentHandoffDashboard';
import AnalyticsDashboard from './AnalyticsDashboard';
import EntityPermissionManager from './EntityPermissionManager';
import ChannelDeploymentManager from './ChannelDeploymentManager';
import { generateId, useAutoSave, useUndoRedo, validateWorkflow, CHANNEL_TYPES, STEP_TYPES } from './ChatbotDesignerUtils';
import { useConfirm, useToast, ValidationModal } from './ChatbotDesignerModals';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ FLOW GROUPING ENGINE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ── Color palette that rotates for any number of groups ──
const GROUP_PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
  '#84cc16', '#e11d48', '#0ea5e9', '#a855f7', '#d97706',
  '#059669', '#7c3aed', '#dc2626', '#0284c7', '#c026d3',
];

/**
 * Humanize a prefix into a readable group name.
 * "mc_sched_" → "Mc Sched", "contact_update_" → "Contact Update"
 */
const humanizePrefix = (prefix) => {
  return prefix
    .replace(/[_-]+$/, '')       // strip trailing separators
    .replace(/[_-]+/g, ' ')      // separators to spaces
    .replace(/\b\w/g, c => c.toUpperCase()); // title case
};

/**
 * Auto-detect flow groups from node IDs using three generic strategies:
 *
 *  1. PREFIX MINING — Finds common prefixes shared by 2+ nodes.
 *     Works for any naming convention (snake_case, camelCase, kebab-case).
 *     No hardcoded prefix list; discovers them from the actual node IDs.
 *
 *  2. CONNECTED COMPONENTS (BFS) — For remaining ungrouped nodes,
 *     clusters tightly connected subgraphs into groups.
 *
 *  3. ENTRY-POINT DETECTION — Nodes with zero in-degree become the
 *     root of a "Main Flow" group along with other unassigned nodes.
 *
 * Colors are assigned from a rotating palette — no hardcoded mapping.
 */
const detectFlowGroups = (nodes, edges) => {
  if (!nodes.length) return [];

  // ── Build adjacency ──
  const adj = {};
  const reverseAdj = {};
  const inDegree = {};
  nodes.forEach(n => { adj[n.id] = []; reverseAdj[n.id] = []; inDegree[n.id] = 0; });
  edges.forEach(e => {
    if (adj[e.source]) adj[e.source].push(e.target);
    if (reverseAdj[e.target]) reverseAdj[e.target].push(e.source);
    if (inDegree[e.target] !== undefined) inDegree[e.target]++;
  });

  // ════════════════════════════════════════════════════════
  // STRATEGY 1: Prefix Mining
  // Extract all candidate prefixes from node IDs and find
  // ones shared by 2+ nodes. Pick longest non-overlapping.
  // ════════════════════════════════════════════════════════

  const extractPrefixes = (nodeId) => {
    const prefixes = [];
    // Split on common separators: _ - camelCase boundaries
    const parts = nodeId.split(/[_\-]+/).filter(Boolean);
    // Also try camelCase split
    const camelParts = nodeId.replace(/([a-z])([A-Z])/g, '$1_$2').split(/[_\-]+/).filter(Boolean);
    const allParts = parts.length >= camelParts.length ? parts : camelParts;

    // Generate prefixes from 1..N-1 parts (don't use the full ID as a prefix)
    for (let i = 1; i < allParts.length; i++) {
      const prefix = allParts.slice(0, i).join('_') + '_';
      prefixes.push(prefix);
    }
    return prefixes;
  };

  // Count how many nodes each prefix matches
  const prefixCounts = {};
  nodes.forEach(n => {
    const prefixes = extractPrefixes(n.id);
    prefixes.forEach(p => {
      if (!prefixCounts[p]) prefixCounts[p] = new Set();
      prefixCounts[p].add(n.id);
    });
  });

  // Filter: prefix must match 2+ nodes to form a group
  const validPrefixes = Object.entries(prefixCounts)
    .filter(([_, nodeSet]) => nodeSet.size >= 2)
    .map(([prefix, nodeSet]) => ({ prefix, nodes: [...nodeSet] }))
    .sort((a, b) => b.prefix.length - a.prefix.length); // longest first

  // Greedy assignment: longest prefix wins, each node assigned once
  const nodeToGroup = {};
  const prefixGroups = [];

  validPrefixes.forEach(({ prefix, nodes: matchedNodes }) => {
    // Only keep nodes not yet assigned
    const available = matchedNodes.filter(nid => !nodeToGroup[nid]);
    if (available.length < 2) return;

    // Check: is this prefix a sub-prefix of an already-created group?
    // If so, skip (the longer prefix already captured these nodes)
    const groupId = `group_${prefix}`;
    prefixGroups.push({ id: groupId, prefix, nodeIds: available });
    available.forEach(nid => { nodeToGroup[nid] = groupId; });
  });

  // ════════════════════════════════════════════════════════
  // STRATEGY 2: Connected Components for ungrouped nodes
  // BFS from each ungrouped node to find clusters
  // ════════════════════════════════════════════════════════

  const ungrouped = nodes.filter(n => !nodeToGroup[n.id]);

  if (ungrouped.length > 0) {
    // Build undirected adjacency for just ungrouped nodes
    const ungroupedSet = new Set(ungrouped.map(n => n.id));
    const undirAdj = {};
    ungrouped.forEach(n => { undirAdj[n.id] = []; });
    edges.forEach(e => {
      if (ungroupedSet.has(e.source) && ungroupedSet.has(e.target)) {
        undirAdj[e.source]?.push(e.target);
        undirAdj[e.target]?.push(e.source);
      }
    });

    const visited = new Set();
    const components = [];

    ungrouped.forEach(n => {
      if (visited.has(n.id)) return;
      // BFS to find connected component
      const component = [];
      const queue = [n.id];
      while (queue.length > 0) {
        const current = queue.shift();
        if (visited.has(current)) continue;
        visited.add(current);
        component.push(current);
        (undirAdj[current] || []).forEach(neighbor => {
          if (!visited.has(neighbor)) queue.push(neighbor);
        });
      }
      components.push(component);
    });

    // Small components (1-2 nodes) merge into a "Main Flow" catch-all;
    // larger components become their own groups
    const mainNodes = [];
    components.forEach((comp, i) => {
      if (comp.length <= 2) {
        mainNodes.push(...comp);
      } else {
        const groupId = `component_${i}`;
        prefixGroups.push({ id: groupId, prefix: null, nodeIds: comp });
        comp.forEach(nid => { nodeToGroup[nid] = groupId; });
      }
    });

    // Anything still ungrouped → Main Flow
    if (mainNodes.length > 0) {
      prefixGroups.unshift({
        id: 'main',
        prefix: null,
        nodeIds: mainNodes,
        isMain: true,
      });
      mainNodes.forEach(nid => { nodeToGroup[nid] = 'main'; });
    }
  }

  // ════════════════════════════════════════════════════════
  // BUILD FINAL GROUPS with auto-assigned colors
  // ════════════════════════════════════════════════════════

  let colorIdx = 0;
  const groups = prefixGroups.map(pg => {
    const color = GROUP_PALETTE[colorIdx % GROUP_PALETTE.length];
    colorIdx++;

    // Generate human-readable name
    let name;
    if (pg.isMain) {
      name = 'Main Flow';
    } else if (pg.prefix) {
      name = humanizePrefix(pg.prefix);
    } else {
      // Component group — name from first node's label
      const firstNode = nodes.find(n => n.id === pg.nodeIds[0]);
      const label = firstNode?.data?.label || pg.nodeIds[0];
      name = label.includes(' ') ? label.split(' ')[0] + ' Group' : label + ' Group';
    }

    return {
      id: pg.id,
      name,
      color,
      nodes: pg.nodeIds,
      isMain: pg.isMain || false,
      prefix: pg.prefix || null,
    };
  });

  return groups;
};

// Allow users to manually assign/override groups
const manualGroupAssignment = (nodes, existingGroups, customGroups) => {
  if (!customGroups || !customGroups.length) return existingGroups;
  // customGroups: [{ id, name, color, nodeIds: [] }]
  const manualMap = {};
  customGroups.forEach(g => g.nodeIds.forEach(nid => { manualMap[nid] = g.id; }));
  
  return existingGroups.map(g => ({
    ...g,
    nodes: g.nodes.filter(nid => !manualMap[nid] || manualMap[nid] === g.id),
  })).concat(customGroups.map(g => ({
    ...g,
    nodes: g.nodeIds,
  }))).filter(g => g.nodes.length > 0);
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ AUTO-LAYOUT ENGINE (Dagre-like hierarchical layout)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const autoLayoutNodes = (nodes, edges, direction = 'TB', spacing = { x: 280, y: 150 }) => {
  if (!nodes.length) return nodes;
  
  // Build adjacency & in-degree
  const adj = {};
  const inDegree = {};
  const nodeMap = {};
  nodes.forEach(n => { adj[n.id] = []; inDegree[n.id] = 0; nodeMap[n.id] = n; });
  edges.forEach(e => {
    if (adj[e.source]) { adj[e.source].push(e.target); }
    if (inDegree[e.target] !== undefined) { inDegree[e.target]++; }
  });

  // Topological sort via Kahn's algorithm
  const queue = [];
  nodes.forEach(n => { if (inDegree[n.id] === 0) queue.push(n.id); });
  
  const levels = {};
  const visited = new Set();
  let maxLevel = 0;

  // BFS level assignment
  const bfsQueue = [...queue];
  queue.forEach(id => { levels[id] = 0; });
  
  while (bfsQueue.length > 0) {
    const current = bfsQueue.shift();
    if (visited.has(current)) continue;
    visited.add(current);
    
    (adj[current] || []).forEach(next => {
      const nextLevel = (levels[current] || 0) + 1;
      if (!levels[next] || nextLevel > levels[next]) {
        levels[next] = nextLevel;
      }
      maxLevel = Math.max(maxLevel, nextLevel);
      bfsQueue.push(next);
    });
  }

  // Assign unvisited nodes
  nodes.forEach(n => {
    if (levels[n.id] === undefined) levels[n.id] = maxLevel + 1;
  });

  // Group by level
  const levelBuckets = {};
  nodes.forEach(n => {
    const lvl = levels[n.id] || 0;
    if (!levelBuckets[lvl]) levelBuckets[lvl] = [];
    levelBuckets[lvl].push(n.id);
  });

  // Position nodes
  return nodes.map(n => {
    const lvl = levels[n.id] || 0;
    const bucket = levelBuckets[lvl];
    const idx = bucket.indexOf(n.id);
    const totalInLevel = bucket.length;
    const offsetX = (idx - (totalInLevel - 1) / 2) * spacing.x;

    const position = direction === 'TB'
      ? { x: 400 + offsetX, y: 100 + lvl * spacing.y }
      : { x: 100 + lvl * spacing.x, y: 400 + offsetX };

    return { ...n, position };
  });
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ PATH TRACER — Highlights a conversation path through the workflow
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const tracePathFromNode = (startNodeId, nodes, edges, maxDepth = 30) => {
  const path = { nodeIds: new Set(), edgeIds: new Set() };
  const adj = {};
  edges.forEach(e => {
    if (!adj[e.source]) adj[e.source] = [];
    adj[e.source].push(e);
  });

  const visit = (nodeId, depth) => {
    if (depth > maxDepth || path.nodeIds.has(nodeId)) return;
    path.nodeIds.add(nodeId);
    (adj[nodeId] || []).forEach(edge => {
      path.edgeIds.add(edge.id);
      visit(edge.target, depth + 1);
    });
  };
  visit(startNodeId, 0);
  return path;
};

const tracePathToNode = (targetNodeId, nodes, edges, maxDepth = 30) => {
  const path = { nodeIds: new Set(), edgeIds: new Set() };
  const reverseAdj = {};
  edges.forEach(e => {
    if (!reverseAdj[e.target]) reverseAdj[e.target] = [];
    reverseAdj[e.target].push(e);
  });

  const visit = (nodeId, depth) => {
    if (depth > maxDepth || path.nodeIds.has(nodeId)) return;
    path.nodeIds.add(nodeId);
    (reverseAdj[nodeId] || []).forEach(edge => {
      path.edgeIds.add(edge.id);
      visit(edge.source, depth + 1);
    });
  };
  visit(targetNodeId, 0);
  return path;
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ FLOW NAVIGATOR SIDEBAR — Sub-flow tree, search, group management
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const FlowNavigator = ({
  nodes, edges, groups, activeGroup, onSelectGroup, onSelectNode,
  searchTerm, onSearchChange, onFocusNode, onTracePath,
  collapsedGroups, onToggleCollapse, selectedNodeId,
  nodeTypeFilter, onNodeTypeFilterChange,
}) => {
  const nodeMap = useMemo(() => {
    const map = {};
    nodes.forEach(n => { map[n.id] = n; });
    return map;
  }, [nodes]);

  const filteredNodes = useMemo(() => {
    let result = nodes;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(n =>
        n.id.toLowerCase().includes(term) ||
        n.data.label.toLowerCase().includes(term) ||
        (n.data.stepData?.message?.toLowerCase().includes(term))
      );
    }
    if (nodeTypeFilter && nodeTypeFilter !== 'all') {
      result = result.filter(n => n.data.stepData?.message_type === nodeTypeFilter);
    }
    return result;
  }, [nodes, searchTerm, nodeTypeFilter]);

  const getTypeIcon = (type) => {
    const cls = "h-3.5 w-3.5";
    switch (type) {
      case 'text': return <MessageSquare className={cls} />;
      case 'buttons': return <MousePointer className={cls} />;
      case 'options': return <List className={cls} />;
      case 'image': return <Image className={cls} />;
      case 'video': return <Video className={cls} />;
      case 'handoff': return <UserCheck className={cls} />;
      case 'ai_response': return <Bot className={cls} />;
      default: return <Zap className={cls} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'text': return 'text-blue-500';
      case 'buttons': return 'text-emerald-500';
      case 'options': return 'text-violet-500';
      case 'handoff': return 'text-amber-500';
      case 'ai_response': return 'text-purple-500';
      default: return 'text-gray-500';
    }
  };

  const messageTypes = useMemo(() => {
    const types = new Set();
    nodes.forEach(n => types.add(n.data.stepData?.message_type));
    return ['all', ...Array.from(types).filter(Boolean)];
  }, [nodes]);

  return (
    <div className="flex flex-col h-full bg-gray-50 border-r border-gray-200" style={{ width: 280 }}>
      {/* Header */}
      <div className="px-3 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
            <Compass className="h-3.5 w-3.5" />
            Flow Navigator
          </h3>
          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
            {nodes.length} nodes
          </span>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search nodes..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Type Filter */}
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          {messageTypes.slice(0, 6).map(type => (
            <button
              key={type}
              onClick={() => onNodeTypeFilterChange(type === nodeTypeFilter ? 'all' : type)}
              className={`text-[10px] px-2 py-0.5 rounded-full transition-all ${
                type === nodeTypeFilter
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {type === 'all' ? 'All' : type}
            </button>
          ))}
        </div>
      </div>

      {/* Group Tree */}
      <div className="flex-1 overflow-y-auto">
        {/* Show All button */}
        <button
          onClick={() => onSelectGroup(null)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors border-b border-gray-100 ${
            !activeGroup ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
          <span>All Flows</span>
          <span className="ml-auto text-[10px] text-gray-400">{nodes.length}</span>
        </button>

        {groups.map(group => {
          const isCollapsed = collapsedGroups.has(group.id);
          const isActive = activeGroup === group.id;
          const groupNodes = searchTerm
            ? filteredNodes.filter(n => group.nodes.includes(n.id))
            : nodes.filter(n => group.nodes.includes(n.id));

          if (searchTerm && groupNodes.length === 0) return null;

          return (
            <div key={group.id} className="border-b border-gray-100">
              {/* Group Header */}
              <div
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-all cursor-pointer ${
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => onSelectGroup(isActive ? null : group.id)}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: group.color }}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleCollapse(group.id); }}
                  className="p-0.5 hover:bg-gray-200 rounded"
                >
                  {isCollapsed
                    ? <ChevronRight className="h-3 w-3" />
                    : <ChevronDown className="h-3 w-3" />
                  }
                </button>
                <span className="font-medium truncate flex-1 text-left">{group.name}</span>
                <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {group.nodes.length}
                </span>
              </div>

              {/* Group Nodes */}
              {!isCollapsed && (
                <div className="pb-1">
                  {groupNodes.map(node => (
                    <button
                      key={node.id}
                      className={`w-full flex items-center gap-2 px-3 pl-8 py-1.5 text-[11px] transition-all ${
                        selectedNodeId === node.id
                          ? 'bg-blue-100 text-blue-800'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                      }`}
                      onClick={() => onFocusNode(node.id)}
                      onDoubleClick={() => onSelectNode(node)}
                    >
                      <span className={getTypeColor(node.data.stepData?.message_type)}>
                        {getTypeIcon(node.data.stepData?.message_type)}
                      </span>
                      <span className="truncate flex-1 text-left">{node.data.label}</span>
                      <code className="text-[9px] text-gray-400 font-mono">{node.id.substring(0, 8)}</code>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Stats */}
      <div className="px-3 py-2 bg-white border-t border-gray-200 text-[10px] text-gray-500 flex items-center justify-between">
        <span>{nodes.length} steps · {edges.length} connections</span>
        <span>{groups.length} groups</span>
      </div>
    </div>
  );
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ BREADCRUMB BAR — Shows current location in the workflow hierarchy
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const BreadcrumbBar = ({ workflowName, activeGroup, groups, selectedNode, onNavigate }) => {
  const activeGroupData = groups.find(g => g.id === activeGroup);

  return (
    <div className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 text-xs text-gray-600">
      <button
        onClick={() => onNavigate('root')}
        className="hover:text-blue-600 transition-colors flex items-center gap-1"
      >
        <Workflow className="h-3 w-3" />
        <span className="max-w-32 truncate">{workflowName}</span>
      </button>

      {activeGroupData && (
        <>
          <ChevronRight className="h-3 w-3 text-gray-300" />
          <button
            onClick={() => onNavigate('group', activeGroup)}
            className="hover:text-blue-600 transition-colors flex items-center gap-1"
          >
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: activeGroupData.color }} />
            <span>{activeGroupData.name}</span>
          </button>
        </>
      )}

      {selectedNode && (
        <>
          <ChevronRight className="h-3 w-3 text-gray-300" />
          <span className="text-blue-700 font-medium flex items-center gap-1">
            <Target className="h-3 w-3" />
            {selectedNode.data.label}
          </span>
        </>
      )}
    </div>
  );
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ FLOW OVERVIEW MAP — Bird's-eye view showing group connections
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const FlowOverviewMap = ({ groups, nodes, edges, onSelectGroup, activeGroup }) => {
  // Compute inter-group edges
  const groupOfNode = useMemo(() => {
    const map = {};
    groups.forEach(g => g.nodes.forEach(nid => { map[nid] = g.id; }));
    return map;
  }, [groups]);

  const interGroupEdges = useMemo(() => {
    const connections = {};
    edges.forEach(e => {
      const sg = groupOfNode[e.source];
      const tg = groupOfNode[e.target];
      if (sg && tg && sg !== tg) {
        const key = `${sg}->${tg}`;
        connections[key] = (connections[key] || 0) + 1;
      }
    });
    return connections;
  }, [edges, groupOfNode]);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 max-w-md">
      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
        <Map className="h-3.5 w-3.5" />
        Flow Overview
      </h4>
      <div className="space-y-1.5">
        {groups.map(g => (
          <button
            key={g.id}
            onClick={() => onSelectGroup(g.id)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${
              activeGroup === g.id
                ? 'ring-2 ring-blue-400 bg-blue-50'
                : 'hover:bg-gray-50 border border-gray-100'
            }`}
          >
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: g.color }}
            />
            <span className="font-medium text-gray-800 flex-1 text-left">{g.name}</span>
            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
              {g.nodes.length}
            </span>
          </button>
        ))}
      </div>
      
      {/* Inter-group connections summary */}
      {Object.keys(interGroupEdges).length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <h5 className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Cross-flow Links</h5>
          <div className="space-y-1">
            {Object.entries(interGroupEdges).slice(0, 5).map(([key, count]) => {
              const [fromId, toId] = key.split('->');
              const fromGroup = groups.find(g => g.id === fromId);
              const toGroup = groups.find(g => g.id === toId);
              return (
                <div key={key} className="flex items-center gap-1.5 text-[10px] text-gray-500">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: fromGroup?.color }} />
                  <ArrowRight className="h-2.5 w-2.5" />
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: toGroup?.color }} />
                  <span className="text-gray-400">×{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ ENHANCED TOOLBAR — Layout, trace, focus actions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const EnhancedToolbar = ({
  onAutoLayout, onTraceForward, onTraceBackward, onClearTrace,
  onFitView, onToggleOverview, isTracing, layoutDirection, onLayoutDirectionChange,
  onCollapseAll, onExpandAll, selectedNodeId,
}) => {
  return (
    <div className="flex items-center gap-1 bg-white rounded-lg shadow-md border border-gray-200 p-1">
      {/* Layout */}
      <div className="flex items-center gap-0.5 border-r border-gray-200 pr-1">
        <button
          onClick={onAutoLayout}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
          title="Auto-layout nodes"
        >
          <LayoutGrid className="h-4 w-4" />
        </button>
        <select
          value={layoutDirection}
          onChange={(e) => onLayoutDirectionChange(e.target.value)}
          className="text-[10px] border border-gray-200 rounded px-1 py-1 bg-white"
          title="Layout direction"
        >
          <option value="TB">↓ Top→Bottom</option>
          <option value="LR">→ Left→Right</option>
        </select>
      </div>

      {/* Trace */}
      <div className="flex items-center gap-0.5 border-r border-gray-200 pr-1">
        <button
          onClick={onTraceForward}
          disabled={!selectedNodeId}
          className={`p-1.5 rounded transition-colors ${
            selectedNodeId
              ? 'hover:bg-emerald-100 text-emerald-600 hover:text-emerald-800'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Trace path forward from selected node"
        >
          <Route className="h-4 w-4" />
        </button>
        <button
          onClick={onTraceBackward}
          disabled={!selectedNodeId}
          className={`p-1.5 rounded transition-colors ${
            selectedNodeId
              ? 'hover:bg-violet-100 text-violet-600 hover:text-violet-800'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Trace path backward to selected node"
        >
          <Waypoints className="h-4 w-4" />
        </button>
        {isTracing && (
          <button
            onClick={onClearTrace}
            className="p-1.5 rounded hover:bg-red-100 text-red-500 hover:text-red-700 transition-colors"
            title="Clear path trace"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* View */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={onFitView}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
          title="Fit view"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <button
          onClick={onToggleOverview}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
          title="Toggle flow overview map"
        >
          <Map className="h-4 w-4" />
        </button>
        <button
          onClick={onCollapseAll}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
          title="Collapse all groups"
        >
          <ChevronsUp className="h-4 w-4" />
        </button>
        <button
          onClick={onExpandAll}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
          title="Expand all groups"
        >
          <ChevronsDown className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ ENHANCED NODE COMPONENT — With group color indicator
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const EnhancedStepNode = ({ data, selected }) => {
  const stepData = data.stepData || {};
  const hasButtons = stepData.buttons && stepData.buttons.length > 0;
  const hasOptions = stepData.options && stepData.options.length > 0;
  const isHighlighted = data._highlighted;
  const isDimmed = data._dimmed;
  const groupColor = data._groupColor;

  const getStepIcon = (messageType) => {
    const iconProps = { className: "h-4 w-4" };
    switch (messageType) {
      case 'text': return <MessageSquare {...iconProps} />;
      case 'buttons': return <MousePointer {...iconProps} />;
      case 'options': return <List {...iconProps} />;
      case 'image': return <Image {...iconProps} />;
      case 'video': return <Video {...iconProps} />;
      case 'audio': return <BoomBox {...iconProps} />;
      case 'carousel': return <Layers {...iconProps} />;
      case 'ai_response': return <Bot {...iconProps} />;
      case 'handoff': return <UserCheck {...iconProps} />;
      case 'file_upload': return <Paperclip {...iconProps} />;
      case 'sheet_read': return <Download {...iconProps} />;
      case 'sheet_write': return <Upload {...iconProps} />;
      case 'table_read': return <Database {...iconProps} />;
      case 'table_write': return <HardDrive {...iconProps} />;
      case 'request_callback': return <PhoneCall {...iconProps} />;
      case 'router': return <GitBranch {...iconProps} />;
      case 'api_sequence': return <ListOrdered {...iconProps} />;
      case 'eod_sync_job': return <RefreshCw {...iconProps} />;
      case 'reminder_job': return <Bell {...iconProps} />;
      default: return <Zap {...iconProps} />;
    }
  };

  const getStepColor = (messageType) => {
    switch (messageType) {
      case 'text': return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'buttons': return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      case 'options': return 'bg-violet-50 border-violet-200 text-violet-700';
      case 'image': return 'bg-orange-50 border-orange-200 text-orange-700';
      case 'video': return 'bg-red-50 border-red-200 text-red-700';
      case 'audio': return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'carousel': return 'bg-pink-50 border-pink-200 text-pink-700';
      case 'ai_response': return 'bg-purple-50 border-purple-200 text-purple-700';
      case 'handoff': return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'file_upload': return 'bg-cyan-50 border-cyan-200 text-cyan-700';
      case 'sheet_read': return 'bg-lime-50 border-lime-200 text-lime-700';
      case 'sheet_write': return 'bg-rose-50 border-rose-200 text-rose-700';
      case 'table_read': return 'bg-sky-50 border-sky-200 text-sky-700';
      case 'table_write': return 'bg-teal-50 border-teal-200 text-teal-700';
      case 'request_callback': return 'bg-orange-50 border-orange-200 text-orange-700';
      case 'router': return 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700';
      case 'api_sequence': return 'bg-green-50 border-green-200 text-green-700';
      case 'eod_sync_job': return 'bg-indigo-50 border-indigo-200 text-indigo-700';
      case 'reminder_job': return 'bg-violet-50 border-violet-200 text-violet-700';
      default: return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  return (
    <div
      className={`relative rounded-xl border-2 shadow-sm transition-all duration-200 ${getStepColor(stepData.message_type)} ${
        selected ? 'ring-2 ring-blue-500 shadow-lg scale-105' : ''
      } ${isHighlighted ? 'ring-2 ring-amber-400 shadow-amber-100 shadow-lg' : ''} ${
        isDimmed ? 'opacity-25' : ''
      }`}
      style={{
        minWidth: 200,
        maxWidth: 260,
        borderLeftWidth: groupColor ? 4 : 2,
        borderLeftColor: groupColor || undefined,
      }}
    >
      {/* Input Handles — edges use id="top-target" and id="left-target" */}
      <Handle type="target" position={Position.Top} id="top-target" className="!bg-blue-500 !w-3 !h-3 !border-2 !border-white" />
      <Handle type="target" position={Position.Left} id="left-target" className="!bg-purple-500 !w-2.5 !h-2.5 !border-2 !border-white" />

      {/* Node Content */}
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1">
          {getStepIcon(stepData.message_type)}
          <span className="font-semibold text-sm truncate flex-1">{data.label.indexOf('Eod_sync_job Step')===0 ? 'Sync_job Step' : data.label}</span>
        </div>
        {stepData.message && (
          <div className="text-xs opacity-70 mt-1 max-w-xs overflow-hidden text-ellipsis" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {stepData.message.substring(0, 80)}
            {stepData.message.length > 80 ? '...' : ''}
          </div>
        )}
      </div>

      {/* Output Handles — edges use id="bottom-source" and id="right-source" */}
      <Handle type="source" position={Position.Bottom} id="bottom-source" className="!bg-green-500 !w-3 !h-3 !border-2 !border-white" />
      <Handle type="source" position={Position.Right} id="right-source" className="!bg-purple-500 !w-2.5 !h-2.5 !border-2 !border-white" />

      {/* Button handles */}
      {hasButtons && stepData.buttons.map((btn, index) => {
        const totalButtons = stepData.buttons.length;
        const topOffset = ((index + 1) / (totalButtons + 1)) * 100;
        return (
          <Handle
            key={`btn-${index}`}
            type="source"
            position={Position.Right}
            id={`button-${index}`}
            style={{ top: `${topOffset}%` }}
            className="!bg-green-500 !w-2.5 !h-2.5 !border-2 !border-white"
          />
        );
      })}

      {hasOptions && stepData.options.map((opt, index) => {
        const totalOptions = stepData.options.length;
        const topOffset = ((index + 1) / (totalOptions + 1)) * 100;
        return (
          <Handle
            key={`opt-${index}`}
            type="source"
            position={Position.Right}
            id={`option-${index}`}
            style={{ top: `${topOffset}%` }}
            className="!bg-purple-500 !w-2.5 !h-2.5 !border-2 !border-white"
          />
        );
      })}
    </div>
  );
};

const enhancedNodeTypes = {
  stepNode: EnhancedStepNode,
};


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ▎ ENHANCED CANVAS WRAPPER — Integrates all UX enhancements
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const EnhancedCanvasInner = ({
  nodes, edges, setNodes, setEdges,
  onNodesChange, onEdgesChange, onConnect,
  onNodeClick, onEdgeClick, onPaneClick,
  selectedNode, workflowName, showNodeMenu,
  setShowNodeMenu, addNewNode, groups,
}) => {
  const { fitView, setCenter, getZoom } = useReactFlow();

  const [navigatorOpen, setNavigatorOpen] = useState(true);
  const [activeGroup, setActiveGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [nodeTypeFilter, setNodeTypeFilter] = useState('all');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [tracedPath, setTracedPath] = useState(null);
  const [layoutDirection, setLayoutDirection] = useState('TB');
  const [showOverview, setShowOverview] = useState(false);

  // Build group-color lookup
  const groupColorMap = useMemo(() => {
    const map = {};
    groups.forEach(g => g.nodes.forEach(nid => { map[nid] = g.color; }));
    return map;
  }, [groups]);

  // Filter nodes based on active group
  const { visibleNodes, visibleEdges } = useMemo(() => {
    let vNodes = nodes;
    let vEdges = edges;

    if (activeGroup) {
      const group = groups.find(g => g.id === activeGroup);
      if (group) {
        const coreNodes = new Set(group.nodes);
        
        // Find boundary nodes — nodes outside the group that are directly
        // connected to a group node. Show them dimmed as context.
        const boundaryNodes = new Set();
        edges.forEach(e => {
          if (coreNodes.has(e.source) && !coreNodes.has(e.target)) boundaryNodes.add(e.target);
          if (coreNodes.has(e.target) && !coreNodes.has(e.source)) boundaryNodes.add(e.source);
        });

        const allVisible = new Set([...coreNodes, ...boundaryNodes]);
        vNodes = nodes.filter(n => allVisible.has(n.id));
        
        // Show ALL edges where BOTH endpoints are visible
        vEdges = edges.filter(e => allVisible.has(e.source) && allVisible.has(e.target));
      }
    }

    // Apply trace highlighting + group boundary dimming
    vNodes = vNodes.map(n => {
      const isCore = !activeGroup || (groups.find(g => g.id === activeGroup)?.nodes.includes(n.id));
      return {
        ...n,
        data: {
          ...n.data,
          _groupColor: groupColorMap[n.id],
          _highlighted: tracedPath ? tracedPath.nodeIds.has(n.id) : false,
          _dimmed: tracedPath
            ? !tracedPath.nodeIds.has(n.id)
            : (activeGroup && !isCore),  // dim boundary nodes in group focus
        },
      };
    });

    vEdges = vEdges.map(e => {
      const isScheduled = e.data?.transition_type === 'scheduled';
      const baseStyle = e.style || {};
      
      if (tracedPath) {
        const isOnPath = tracedPath.edgeIds.has(e.id);
        return {
          ...e,
          style: isOnPath
            ? { ...baseStyle, stroke: '#f59e0b', strokeWidth: 3 }
            : { ...baseStyle, stroke: '#e5e7eb', strokeWidth: 1, opacity: 0.3 },
          animated: isOnPath,
        };
      }
      
      // Preserve scheduled edge dashed styling
      if (isScheduled) {
        return {
          ...e,
          style: { ...baseStyle, strokeDasharray: baseStyle.strokeDasharray || '8 4' },
        };
      }
      
      return e;
    });

    return { visibleNodes: vNodes, visibleEdges: vEdges };
  }, [nodes, edges, activeGroup, groups, tracedPath, groupColorMap]);

  // Focus on a specific node
  const focusNode = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setCenter(node.position.x + 100, node.position.y + 50, { zoom: 1.2, duration: 500 });
      onNodeClick(null, node);
    }
  }, [nodes, setCenter, onNodeClick]);

  // Auto-layout handler
  const handleAutoLayout = useCallback(() => {
    const targetNodes = activeGroup
      ? nodes.filter(n => groups.find(g => g.id === activeGroup)?.nodes.includes(n.id))
      : nodes;
    const targetEdges = edges.filter(e =>
      targetNodes.some(n => n.id === e.source) && targetNodes.some(n => n.id === e.target)
    );
    const laid = autoLayoutNodes(targetNodes, targetEdges, layoutDirection);
    const laidMap = {};
    laid.forEach(n => { laidMap[n.id] = n.position; });
    setNodes(nds => nds.map(n => laidMap[n.id] ? { ...n, position: laidMap[n.id] } : n));
    setTimeout(() => fitView({ padding: 0.2, duration: 500 }), 100);
  }, [nodes, edges, activeGroup, groups, layoutDirection, setNodes, fitView]);

  // Path tracing
  const handleTraceForward = useCallback(() => {
    if (!selectedNode) return;
    const path = tracePathFromNode(selectedNode.id, nodes, edges);
    setTracedPath(path);
  }, [selectedNode, nodes, edges]);

  const handleTraceBackward = useCallback(() => {
    if (!selectedNode) return;
    const path = tracePathToNode(selectedNode.id, nodes, edges);
    setTracedPath(path);
  }, [selectedNode, nodes, edges]);

  const handleClearTrace = useCallback(() => setTracedPath(null), []);

  // Group selection handler
  const handleSelectGroup = useCallback((groupId) => {
    setActiveGroup(groupId);
    setTracedPath(null);
    if (groupId) {
      setTimeout(() => fitView({ padding: 0.3, duration: 500 }), 100);
    }
  }, [fitView]);

  const toggleCollapse = useCallback((groupId) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  }, []);

  return (
    <div className="flex flex-1 h-full">
      {/* Flow Navigator Sidebar */}
      {navigatorOpen && (
        <FlowNavigator
          nodes={nodes}
          edges={edges}
          groups={groups}
          activeGroup={activeGroup}
          onSelectGroup={handleSelectGroup}
          onSelectNode={(node) => onNodeClick(null, node)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFocusNode={focusNode}
          collapsedGroups={collapsedGroups}
          onToggleCollapse={toggleCollapse}
          selectedNodeId={selectedNode?.id}
          nodeTypeFilter={nodeTypeFilter}
          onNodeTypeFilterChange={setNodeTypeFilter}
        />
      )}

      {/* Main Canvas */}
      <div className="flex-1 flex flex-col relative">
        {/* Breadcrumb */}
        <BreadcrumbBar
          workflowName={workflowName}
          activeGroup={activeGroup}
          groups={groups}
          selectedNode={selectedNode}
          onNavigate={(type, id) => {
            if (type === 'root') { setActiveGroup(null); setTracedPath(null); }
            else if (type === 'group') handleSelectGroup(id);
          }}
        />

        <div className="flex-1 relative">
          <ReactFlow
            nodes={visibleNodes}
            edges={visibleEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            onNodeClick={onNodeClick}
            onPaneClick={(e) => { onPaneClick(e); setTracedPath(null); }}
            nodeTypes={enhancedNodeTypes}
            connectionMode="loose"
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: true,
              style: { strokeWidth: 2, stroke: '#3b82f6' },
            }}
            deleteKeyCode={['Backspace', 'Delete']}
            fitView
            attributionPosition="bottom-left"
            minZoom={0.1}
            maxZoom={2}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e5e7eb" />
            <Controls showInteractive={false} />
            <MiniMap
              nodeColor={(node) => {
                if (node.data._highlighted) return '#f59e0b';
                return groupColorMap[node.id] || '#6b7280';
              }}
              maskColor="rgba(0,0,0,0.08)"
              style={{ border: '1px solid #e5e7eb', borderRadius: 8 }}
            />

            {/* Enhanced Toolbar */}
            <Panel position="top-left">
              <div className="flex flex-col gap-2">
                {/* Toggle Navigator */}
                <button
                  onClick={() => setNavigatorOpen(!navigatorOpen)}
                  className="bg-white rounded-lg shadow-md border border-gray-200 p-2 hover:bg-gray-50 transition-colors"
                  title={navigatorOpen ? "Hide Navigator" : "Show Navigator"}
                >
                  {navigatorOpen
                    ? <PanelLeftClose className="h-4 w-4 text-gray-600" />
                    : <PanelLeftOpen className="h-4 w-4 text-gray-600" />
                  }
                </button>

                {/* Add Step */}
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-1">
                  <button
                    onClick={() => setShowNodeMenu(!showNodeMenu)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Step</span>
                  </button>
                  {showNodeMenu && (
                    <div className="absolute mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-2 space-y-0.5 z-10 min-w-[160px]">
                      {['text', 'buttons', 'options', 'image', 'video', 'audio', 'carousel', 'whatsapp_flow', 'ai_response', 'handoff', 'file_upload', 'sheet_read', 'sheet_write', 'table_read', 'table_write', 'request_callback', 'router', 'api_sequence', 'api_call', 'eod_sync_job', 'reminder_job'].map((type) => (
                        <button
                          key={type}
                          onClick={() => addNewNode(type)}
                          className="w-full text-left px-3 py-1.5 rounded hover:bg-gray-100 capitalize text-sm"
                        >
                          {type=='eod_sync_job' ? 'Sync Job' : type=='api_call' ? 'API Call' : type=='router' ? 'Router' : type=='api_sequence' ? 'Sequenced API' : type.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Enhanced Toolbar */}
                <EnhancedToolbar
                  onAutoLayout={handleAutoLayout}
                  onTraceForward={handleTraceForward}
                  onTraceBackward={handleTraceBackward}
                  onClearTrace={handleClearTrace}
                  onFitView={() => fitView({ padding: 0.2, duration: 500 })}
                  onToggleOverview={() => setShowOverview(!showOverview)}
                  isTracing={!!tracedPath}
                  layoutDirection={layoutDirection}
                  onLayoutDirectionChange={setLayoutDirection}
                  onCollapseAll={() => setCollapsedGroups(new Set(groups.map(g => g.id)))}
                  onExpandAll={() => setCollapsedGroups(new Set())}
                  selectedNodeId={selectedNode?.id}
                />
              </div>
            </Panel>

            {/* Active Group Indicator */}
            {activeGroup && (
              <Panel position="top-center">
                <div className="bg-white rounded-full shadow-md border border-gray-200 px-4 py-1.5 flex items-center gap-2 text-sm">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: groups.find(g => g.id === activeGroup)?.color }}
                  />
                  <span className="font-medium text-gray-700">
                    {groups.find(g => g.id === activeGroup)?.name}
                  </span>
                  <button
                    onClick={() => handleSelectGroup(null)}
                    className="ml-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </Panel>
            )}

            {/* Trace Legend */}
            {tracedPath && (
              <Panel position="bottom-center">
                <div className="bg-white rounded-lg shadow-md border border-gray-200 px-3 py-2 flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-0.5 bg-amber-400 rounded"></div>
                    <span className="text-gray-600">Traced path</span>
                  </div>
                  <span className="text-gray-400">·</span>
                  <span className="text-gray-500">
                    {tracedPath.nodeIds.size} nodes, {tracedPath.edgeIds.size} edges
                  </span>
                  <button
                    onClick={handleClearTrace}
                    className="text-red-500 hover:text-red-700 ml-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </Panel>
            )}
          </ReactFlow>

          {/* Overview Map Overlay */}
          {showOverview && (
            <div className="absolute top-16 right-4 z-20">
              <FlowOverviewMap
                groups={groups}
                nodes={nodes}
                edges={edges}
                onSelectGroup={handleSelectGroup}
                activeGroup={activeGroup}
              />
              <button
                onClick={() => setShowOverview(false)}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Wrap with ReactFlowProvider for useReactFlow hook
const EnhancedCanvas = (props) => (
  <ReactFlowProvider>
    <EnhancedCanvasInner {...props} />
  </ReactFlowProvider>
);

export default EnhancedCanvas;
export { detectFlowGroups, autoLayoutNodes, tracePathFromNode, tracePathToNode, FlowNavigator, BreadcrumbBar, EnhancedToolbar, FlowOverviewMap };